// Telemetry ring (IMPLEMENTATION §2.9 "Telemetry"): rows with the CSV columns `wall_ms, session, tick, event,
// subject, value, extra` (debug/csv.ts). M5 logged the save rows (defs mismatch, corrupt main / bak) into a
// memory ring; M12 adds persistence and the export:
//   - the ring (≤ `cap` rows) holds the rows loaded from storage plus this session's, each with its
//     pre-escaped CSV line, so a flush is one join and the export is the stored text itself;
//   - `flush()` writes the whole ring to `localStorage['gunmerger.telemetry']` as CSV text in ONE write, and
//     only when a row was logged since the last successful write. The app flushes with every autosave (timer,
//     hidden, pagehide): at most one telemetry write per autosave;
//   - `session` = 1 + the highest session number in the stored ring (per device, independent of the save).
// `log` / `rows` keep their M5 signatures: M6 logs its `drag` / `rage_tap` rows through `log`
// (debug/telemetryRows.ts has the row builders). The SimEvent-derived rows come from debug/telemetrySink.ts.
import type { KeyValueStore } from '../save/storage.ts';
import { csvLine, linesToCsv, parseTelemetryCsv, type TelemetryRecord } from './csv.ts';

export type { TelemetryRecord } from './csv.ts';

/** localStorage key of the persisted ring (CSV text). */
export const TELEMETRY_KEY = 'gunmerger.telemetry';

/** What callers log; `wall_ms`, `session` and `tick` are stamped by the ring. */
export interface TelemetryRow {
  event: string;
  subject: string;
  value: number;
  extra: string;
}

export interface Telemetry {
  log(row: { event: string; subject: string; value: number; extra: string }): void;
  /** Copies of every row in the ring (stored + this session), oldest first. */
  rows(): TelemetryRecord[];
  /** M12: this boot's session number. */
  session(): number;
  /** M12: rows logged since the last successful flush. */
  pending(): number;
  /**
   * M12: writes the ring to the store in ONE write when a row was logged since the last successful write.
   * true = written; false = nothing new, no store, or the write failed (the rows stay; the next flush retries).
   */
  flush(): boolean;
  /**
   * Integration fix: removes the stored ring (a game save failed for lack of space: the save needs it more);
   * the rows stay in memory and the next successful flush writes them again.
   */
  unstore(): void;
  /** M12: the ring as CSV text (header + rows, RFC 4180, CRLF). */
  csv(): string;
}

export interface TelemetryOptions {
  /** Ring size (ui.json debug.telemetryCap). */
  cap: number;
  /** Session number without a store; with a store it is the minimum (1 + the stored maximum wins). */
  session: number;
  /** Wall clock ms (Date.now in the app). */
  now(): number;
  /** The sim tick to stamp rows with. */
  tick(): number;
  /** M12: where the ring persists (localStorage in the app); omitted = memory only (the M5 behaviour). */
  store?: KeyValueStore;
  /** M12: storage key (default TELEMETRY_KEY). */
  key?: string;
  /** M12: a longer `subject` / `extra` is cut to this many characters (ui.json debug.telemetryTextMax). */
  textMax?: number;
}

/** The stored ring; a bad row is skipped (and counted), only an unreadable file (header, quoting) is dropped. */
function loadStored(
  store: KeyValueStore | undefined,
  key: string,
): { recs: TelemetryRecord[]; bad: number } {
  if (!store) return { recs: [], bad: 0 };
  const text = store.get(key);
  if (text === null || text.length === 0) return { recs: [], bad: 0 };
  let bad = 0;
  try {
    return {
      recs: parseTelemetryCsv(text, () => {
        bad += 1;
      }),
      bad,
    };
  } catch {
    return { recs: [], bad: 1 }; // unreadable (a hand edit): start over; the next flush replaces it
  }
}

/** A whole number for the integer columns (a NaN clock must not make the row unreadable on reload). */
const int = (n: number): number => (Number.isFinite(n) ? Math.round(n) : 0);

export function createTelemetry(o: TelemetryOptions): Telemetry {
  const key = o.key ?? TELEMETRY_KEY;
  const stored = loadStored(o.store, key);
  const recs = o.cap > 0 ? stored.recs.slice(-o.cap) : [];
  const lines = recs.map(csvLine);
  let maxStored = 0;
  for (const r of recs) if (r.session > maxStored) maxStored = r.session;
  const session = o.store ? Math.max(o.session, maxStored + 1) : o.session;
  let unsaved = 0;
  /** The stored copy was removed (unstore): the next flush rewrites the ring even without a new row. */
  let removed = false;
  const cut = (s: string): string =>
    o.textMax !== undefined && s.length > o.textMax ? s.slice(0, o.textMax) : s;
  const tel: Telemetry = {
    log(r) {
      // every row must parse again at the next boot (csv.ts parseTelemetryCsv): integer columns, a non-empty
      // event, a finite value
      const rec: TelemetryRecord = {
        wall_ms: int(o.now()),
        session,
        tick: int(o.tick()),
        event: String(r.event) || 'unknown',
        subject: cut(String(r.subject)),
        value: Number.isFinite(r.value) ? r.value : 0,
        extra: cut(String(r.extra)),
      };
      recs.push(rec);
      lines.push(csvLine(rec));
      if (recs.length > o.cap) {
        const drop = recs.length - o.cap;
        recs.splice(0, drop);
        lines.splice(0, drop);
      }
      unsaved += 1;
    },
    rows: () => recs.map((r) => ({ ...r })),
    session: () => session,
    pending: () => unsaved,
    flush() {
      if (!o.store || (unsaved === 0 && !removed)) return false;
      const ok = o.store.set(key, linesToCsv(lines));
      if (ok) {
        unsaved = 0;
        removed = false;
      }
      return ok;
    },
    unstore() {
      if (!o.store) return;
      o.store.remove(key);
      removed = true;
    },
    csv: () => linesToCsv(lines),
  };
  // skipped stored rows: say so (value = how many); the next flush writes the cleaned ring
  if (stored.bad > 0)
    tel.log({
      event: 'error',
      subject: 'telemetry',
      value: stored.bad,
      extra: 'unreadable stored rows dropped',
    });
  return tel;
}
